import TextArea from "antd/lib/input/TextArea";
import parse from 'html-react-parser'
import { useEffect, useRef } from "react";

function IrbNotificationTemplate(props: {
  distribution: any;
  report: any;
  additionalText: string;
  coverText: any;
  onAdditionalTextChange: any;
  notificationAckTemplate?: string | null
  showAdditionalText: any;
  notificationTemplate?: string | null
  isEditable?: boolean;
  onMainBodyChange?: (body: { html: string; text: string }) => void;
}) {
  const {
    distribution,
    report,
    coverText,
    additionalText,
    onAdditionalTextChange,
    notificationTemplate
  } = props;
  const {
    type,
    org,
    product,
    studyTitle,
    studyID,
    safetyDatabaseReportNumber,
  } = report;
  const { siteName, studyNumber } = distribution;

  const template = props.report.IRBECnotificationTemplateHtml ? props.report.IRBECnotificationTemplateHtml : notificationTemplate;

  // Editing is opt-in: the recipient-facing view keeps this template read-only,
  // while the Send Notification screen lets the safety team reword the letter
  // before it goes out - same as the notification and reminder templates.
  const isEditable = props.isEditable === true;
  const editableRef = useRef<HTMLDivElement | null>(null);
  const didSetInitialHtml = useRef<boolean>(false);

  // Whatever is on screen is what gets sent, so report the body on every edit.
  const emitMainBody = () => {
    if (!editableRef.current || !props.onMainBodyChange) return;
    props.onMainBodyChange({
      html: editableRef.current.innerHTML,
      text: editableRef.current.innerText,
    });
  };

  const templateHtml = template
    ? additionalText
      ? template.replace("<wbr></wbr>", `<p>${additionalText}</p>`)
      : template.replace("<wbr></wbr>", ``)
    : "";

  // Written straight to the node (not via children) so a re-render of the parent
  // cannot throw away what the user has typed.
  useEffect(() => {
    if (templateHtml && editableRef.current) {
      editableRef.current.innerHTML = templateHtml;
      didSetInitialHtml.current = true;
      emitMainBody();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateHtml]);

  // No custom template: hand over the default letter below as the initial body.
  useEffect(() => {
    if (editableRef.current && !didSetInitialHtml.current) {
      emitMainBody();
      didSetInitialHtml.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const editableBoxStyle = isEditable
    ? {
      border: "1px solid #ddd",
      padding: "1rem",
      borderRadius: "8px",
      minHeight: "200px",
    }
    : undefined;

  if (coverText) {
    return (
      <div>
        {parse(coverText)}
      </div>
    )
  } else if (template) {
    return <>
      <div
        contentEditable={isEditable}
        suppressContentEditableWarning
        onInput={emitMainBody}
        onBlur={emitMainBody}
        ref={editableRef}
        style={editableBoxStyle}
      />
      {[16, 17].includes(distribution?.role)  && (
        <TextArea
          onChange={(event) => onAdditionalTextChange(event.target.value)}
          style={{ marginBottom: "1rem" }}
          showCount
          maxLength={2000}
          placeholder="Add any additional information, as required"
        />
      )}
    </>
  }
  else {
    return <div
      contentEditable={isEditable}
      suppressContentEditableWarning
      onInput={emitMainBody}
      onBlur={emitMainBody}
      ref={editableRef}
      style={editableBoxStyle}
    >
      <p>Dear {siteName || "{Site Name}"} Colleagues,</p>
      <p>
        {org} is the Sponsor of the clinical study {studyID || "{Study ID}"} -{" "}
        {studyTitle || "{Study Title}"}. {org} is required to inform EC/IRB in
        writing of any suspected, unexpected serious adverse reaction (SUSAR)
        that occurs in a study evaluating the investigational product. Soterius,
        on behalf of {org}, is providing you with a suspected unexpected serious
        adverse reaction (SUSAR) from study {studyID} for {product}.
      </p>

      {[16, 17].includes(distribution?.role) ? (
        <TextArea
          onChange={(event) => onAdditionalTextChange(event.target.value)}
          style={{ marginBottom: "1rem" }}
          showCount
          maxLength={2000}
          placeholder="Add any additional information, as required"
        />
      ) : (
        <p>{additionalText}</p>
      )}

      <p>
        Enclosed, please find the following documents with regards to this{" "}
        {type} day SUSAR: {safetyDatabaseReportNumber}:
      </p>

      <p style={{ paddingLeft: "1.5rem" }}>
        <ol>
          <li>Investigator Notification Letter</li>
          <li>CIOMS/MedWatch Form </li>
        </ol>
      </p>

      <p>
        Please review the attached CIOMS/MedWatch form for this case to as per
        your internal processes and evaluate the risks and benefits of the study {studyNumber ? `(${studyNumber})` : `({Study Number})`}. Please do let us
        know if you foresee a change in, or an impact on the benefit-risk
        balance of the {studyID} study.
      </p>

      <p>
        Thank you very much and feel free to reach out to the Safety Team /
        Medical Monitor as per the Contact Information provided by {org}, should
        you have any questions.
      </p>

      <p>
        Note: Request you to kindly provide your acknowledgement of receipt of
        this communication.
      </p>

      <p>Kind regards, </p>
      <p>Soterius Safety Team</p>
      <p>On behalf of {org} </p>
    </div>
  }


}
export default IrbNotificationTemplate;
